import { useEffect, useState } from 'react';
import { useAuthFileUrl } from '../../../../components/patient-attachments/AuthFile';
import { normalizeCrop } from './applicationFigures';

/**
 * Foto do paciente como figura do mapa: baixa o anexo pelo endpoint
 * autenticado, mede a imagem e devolve o quadro recortado.
 *
 * `background`: { attachmentId, crop } ou null (desenho padrão).
 * @returns {{ figure: object|null, loading: boolean, error: string|null }}
 * `figure`: { src, width, height, viewBox: { x, y, width, height }, photo: true },
 * em pixels da foto; as marcações da vista são frações do `viewBox`.
 */
export function usePhotoFigure(background) {
  const attachmentId = background ? background.attachmentId : null;
  const { url, loading, error } = useAuthFileUrl(attachmentId, 'full', !!attachmentId);
  const [size, setSize] = useState(null);

  useEffect(() => {
    if (!url) {
      setSize(null);
      return undefined;
    }
    let active = true;
    const image = new Image();
    image.onload = () => { if (active) setSize({ url, width: image.naturalWidth, height: image.naturalHeight }); };
    image.onerror = () => { if (active) setSize({ url, width: 0, height: 0 }); };
    image.src = url;
    return () => { active = false; };
  }, [url]);

  if (!attachmentId) return { figure: null, loading: false, error: null };

  const measured = size && size.url === url ? size : null;
  if (error) return { figure: null, loading: false, error };
  if (measured && measured.width === 0) return { figure: null, loading: false, error: 'Não foi possível abrir a foto.' };
  if (!measured) return { figure: null, loading: true, error: null };

  const crop = normalizeCrop(background.crop);
  return {
    figure: {
      src: url,
      width: measured.width,
      height: measured.height,
      photo: true,
      viewBox: {
        x: crop.x * measured.width,
        y: crop.y * measured.height,
        width: crop.w * measured.width,
        height: crop.h * measured.height,
      },
    },
    loading: loading && !measured,
    error: null,
  };
}
